import React, { useState } from "react";
import * as Yup from "yup";
import { ErrorMessage, Field, FieldArray, Form, Formik } from "formik";
import { FaSpinner } from "react-icons/fa";
import Popup from "reactjs-popup";
import Loader from "../../Snippets/Loader";
import { uploadArbitratorDocument } from "../../../Services/ArbitrationManagement/ArbitrationData";

const ArbitrationAction = ({
  isPreviewOpen,
  handleClose,
  handleSubmit,
  isLoadingUpdate,
}) => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  const initialFormValues = {
    arbitratorName: "",
    arbitratorEmail: "",
    arbitratorPhone: "",
    arbitratorWhatsapp: "",
    arbitratorAddress: "",

    claimantName: "",
    claimantEmail: "",
    claimantPhone: "",
    claimantWhatsapp: "",
    claimantAddress: "",

    respondentName: "",
    respondentEmail: "",
    respondentPhone: "",
    respondentWhatsapp: "",
    respondentAddress: "",

    natureOfDispute: "",
    valueOfClaim: "",

    additionalArbitrators: [],
    additionalClaimants: [],
    additionalRespondents: [],
  };

  const validationSchema = Yup.object({
    arbitratorName: Yup.string().required("Required"),
    arbitratorEmail: Yup.string().required("Required").email("Invalid"),
    arbitratorPhone: Yup.string()
      .matches(/^[0-9]{10}$/, "Invalid")
      .required("Required"),
    arbitratorWhatsapp: Yup.string()
      .matches(/^[0-9]{10}$/, "Invalid")
      .required("Required"),
    arbitratorAddress: Yup.string().required("Required"),

    claimantName: Yup.string().required("Required"),
    claimantEmail: Yup.string().email("Invalid").required("Required"),
    claimantPhone: Yup.string()
      .matches(/^[0-9]{10}$/, "Invalid")
      .required("Required"),
    claimantWhatsapp: Yup.string()
      .matches(/^[0-9]{10}$/, "Invalid")
      .required("Required"),
    claimantAddress: Yup.string().required("Required"),

    respondentName: Yup.string().required("Required"),
    respondentEmail: Yup.string().email("Invalid").required("Required"),
    respondentPhone: Yup.string()
      .matches(/^[0-9]{10}$/, "Invalid")
      .required("Required"),
    respondentWhatsapp: Yup.string()
      .matches(/^[0-9]{10}$/, "Invalid")
      .required("Required"),
    respondentAddress: Yup.string().required("Required"),

    natureOfDispute: Yup.string().required("Required"),
    valueOfClaim: Yup.number().required("Required"),
  });

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);

    const MAX_FILES = 10;
    const MAX_SIZE = 20 * 1024 * 1024;

    if (files.length + uploadedFiles.length > MAX_FILES) {
      setUploadError("Maximum 10 files allowed");
      return;
    }

    const totalSize = files.reduce((a, f) => a + f.size, 0);

    if (totalSize > MAX_SIZE) {
      setUploadError("Total size cannot exceed 20MB");
      return;
    }

    try {
      setIsUploading(true);
      setUploadError("");

      const formData = new FormData();

      files.forEach((file) => {
        formData.append("file", file); // append multiple files
      });

      const response = await uploadArbitratorDocument(formData);

      const urls = response?.urls || [];

      setUploadedFiles((prev) => [...prev, ...urls]);
    } catch (err) {
      console.log("Upload Error:", err);
    } finally {
      setIsUploading(false);
    }
  };

  const removeFile = (index) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const CardSection = ({ title, children }) => (
    <div className="bg-white shadow-sm border rounded-lg p-6 mb-6">
      <h3 className="text-lg font-semibold text-gray-700 mb-5">{title}</h3>

      {children}
    </div>
  );

  return (
    <Popup open={isPreviewOpen} modal closeOnDocumentClick={false}>
      {(close) => (
        <div className="fixed inset-0 flex items-center justify-center bg-black/30">
          <div className="bg-white w-full max-w-6xl rounded-xl shadow-lg flex flex-col max-h-[90vh]">
            {/* HEADER */}

            <div className="flex justify-between items-center border-b px-6 py-4">
              <h2 className="text-xl font-semibold">Commence Arbitration</h2>

              <button
                onClick={() => {
                  handleClose();
                  close();
                }}
                className="text-xl font-bold"
              >
                ✕
              </button>
            </div>

            {/* SCROLLABLE CARD BODY */}

            <div className="overflow-y-auto p-6">
              <Formik
                initialValues={initialFormValues}
                validationSchema={validationSchema}
                onSubmit={(values) => handleSubmit(values, uploadedFiles)}
              >
                {({ values }) => (
                  <Form>
                    {/* ARBITRATOR */}

                    <CardSection title="Arbitrator Details">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        <Field
                          name="arbitratorName"
                          placeholder="Name"
                          className="input-style"
                        />
                        <Field
                          name="arbitratorEmail"
                          placeholder="Email"
                          className="input-style"
                        />
                        <Field
                          name="arbitratorPhone"
                          placeholder="Phone"
                          className="input-style"
                        />

                        <Field
                          name="arbitratorWhatsapp"
                          placeholder="WhatsApp"
                          className="input-style"
                        />
                        <Field
                          name="arbitratorAddress"
                          placeholder="Address"
                          className="input-style"
                        />
                      </div>

                      <FieldArray name="additionalArbitrators">
                        {({ push, remove }) => (
                          <div className="mt-5">
                            {values.additionalArbitrators.map((arb, index) => (
                              <div
                                key={index}
                                className="border rounded p-4 mt-4 bg-gray-50"
                              >
                                <div className="grid md:grid-cols-3 gap-4">
                                  <Field
                                    name={`additionalArbitrators.${index}.name`}
                                    placeholder="Name"
                                    className="input-style"
                                  />
                                  <Field
                                    name={`additionalArbitrators.${index}.email`}
                                    placeholder="Email"
                                    className="input-style"
                                  />
                                  <Field
                                    name={`additionalArbitrators.${index}.phone`}
                                    placeholder="Phone"
                                    className="input-style"
                                  />

                                  <Field
                                    name={`additionalArbitrators.${index}.whatsapp`}
                                    placeholder="WhatsApp"
                                    className="input-style"
                                  />
                                  <Field
                                    name={`additionalArbitrators.${index}.address`}
                                    placeholder="Address"
                                    className="input-style"
                                  />
                                </div>

                                <button
                                  type="button"
                                  onClick={() => remove(index)}
                                  className="text-red-600 text-sm mt-3"
                                >
                                  Remove
                                </button>
                              </div>
                            ))}

                            <button
                              type="button"
                              onClick={() =>
                                push({
                                  name: "",
                                  email: "",
                                  phone: "",
                                  whatsapp: "",
                                  address: "",
                                })
                              }
                              className="mt-4 bg-blue-600 text-white px-4 py-1 rounded text-sm"
                            >
                              + Add Arbitrator
                            </button>
                          </div>
                        )}
                      </FieldArray>
                    </CardSection>

                    {/* CLAIMANT */}

                    <CardSection title="Claimant Details">
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                        <Field
                          name="claimantName"
                          placeholder="Name"
                          className="input-style"
                        />
                        <Field
                          name="claimantEmail"
                          placeholder="Email"
                          className="input-style"
                        />
                        <Field
                          name="claimantPhone"
                          placeholder="Phone"
                          className="input-style"
                        />

                        <Field
                          name="claimantWhatsapp"
                          placeholder="WhatsApp"
                          className="input-style"
                        />
                        <Field
                          name="claimantAddress"
                          placeholder="Address"
                          className="input-style"
                        />
                      </div>

                      <FieldArray name="additionalClaimants">
                        {({ push, remove }) => (
                          <div className="mt-5">
                            {values.additionalClaimants.map((c, index) => (
                              <div
                                key={index}
                                className="border rounded p-4 mt-4 bg-gray-50"
                              >
                                <div className="grid md:grid-cols-3 gap-4">
                                  <Field
                                    name={`additionalClaimants.${index}.name`}
                                    placeholder="Name"
                                    className="input-style"
                                  />
                                  <Field
                                    name={`additionalClaimants.${index}.email`}
                                    placeholder="Email"
                                    className="input-style"
                                  />
                                  <Field
                                    name={`additionalClaimants.${index}.phone`}
                                    placeholder="Phone"
                                    className="input-style"
                                  />

                                  <Field
                                    name={`additionalClaimants.${index}.whatsapp`}
                                    placeholder="WhatsApp"
                                    className="input-style"
                                  />
                                  <Field
                                    name={`additionalClaimants.${index}.address`}
                                    placeholder="Address"
                                    className="input-style"
                                  />
                                </div>

                                <button
                                  type="button"
                                  onClick={() => remove(index)}
                                  className="text-red-600 text-sm mt-3"
                                >
                                  Remove
                                </button>
                              </div>
                            ))}

                            <button
                              type="button"
                              onClick={() =>
                                push({
                                  name: "",
                                  email: "",
                                  phone: "",
                                  whatsapp: "",
                                  address: "",
                                })
                              }
                              className="mt-4 bg-blue-600 text-white px-4 py-1 rounded text-sm"
                            >
                              + Add Claimant
                            </button>
                          </div>
                        )}
                      </FieldArray>
                    </CardSection>

                    {/* RESPONDENT */}

                    <CardSection title="Respondent Details">
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                        <Field
                          name="respondentName"
                          placeholder="Name"
                          className="input-style"
                        />
                        <Field
                          name="respondentEmail"
                          placeholder="Email"
                          className="input-style"
                        />
                        <Field
                          name="respondentPhone"
                          placeholder="Phone"
                          className="input-style"
                        />

                        <Field
                          name="respondentWhatsapp"
                          placeholder="WhatsApp"
                          className="input-style"
                        />
                        <Field
                          name="respondentAddress"
                          placeholder="Address"
                          className="input-style"
                        />
                      </div>

                      <FieldArray name="additionalRespondents">
                        {({ push, remove }) => (
                          <div className="mt-5">
                            {values.additionalRespondents.map((r, index) => (
                              <div
                                key={index}
                                className="border rounded p-4 mt-4 bg-gray-50"
                              >
                                <div className="grid md:grid-cols-3 gap-4">
                                  <Field
                                    name={`additionalRespondents.${index}.name`}
                                    placeholder="Name"
                                    className="input-style"
                                  />
                                  <Field
                                    name={`additionalRespondents.${index}.email`}
                                    placeholder="Email"
                                    className="input-style"
                                  />
                                  <Field
                                    name={`additionalRespondents.${index}.phone`}
                                    placeholder="Phone"
                                    className="input-style"
                                  />

                                  <Field
                                    name={`additionalRespondents.${index}.whatsapp`}
                                    placeholder="WhatsApp"
                                    className="input-style"
                                  />
                                  <Field
                                    name={`additionalRespondents.${index}.address`}
                                    placeholder="Address"
                                    className="input-style"
                                  />
                                </div>

                                <button
                                  type="button"
                                  onClick={() => remove(index)}
                                  className="text-red-600 text-sm mt-3"
                                >
                                  Remove
                                </button>
                              </div>
                            ))}

                            <button
                              type="button"
                              onClick={() =>
                                push({
                                  name: "",
                                  email: "",
                                  phone: "",
                                  whatsapp: "",
                                  address: "",
                                })
                              }
                              className="mt-4 bg-blue-600 text-white px-4 py-1 rounded text-sm"
                            >
                              + Add Respondent
                            </button>
                          </div>
                        )}
                      </FieldArray>
                    </CardSection>

                    {/* ARBITRATION */}

                    <CardSection title="Arbitration Details">
                      <div className="grid md:grid-cols-2 gap-5">
                        <Field
                          name="natureOfDispute"
                          placeholder="Nature of dispute"
                          className="input-style"
                        />
                        <Field
                          name="valueOfClaim"
                          placeholder="Value of claim"
                          className="input-style"
                        />
                      </div>
                    </CardSection>

                    {/* FILE UPLOAD */}

                    <CardSection title="Upload Arbitration Petition">
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                        <input
                          type="file"
                          multiple
                          onChange={handleFileUpload}
                          disabled={isUploading}
                        />

                        <p className="text-sm text-gray-500 mt-2">
                          Max 10 files • 20MB
                        </p>
                      </div>

                      {uploadError && (
                        <p className="text-red-500 mt-2 text-sm">
                          {uploadError}
                        </p>
                      )}

                      {uploadedFiles.length > 0 && (
                        <div className="mt-4 space-y-2">
                          {uploadedFiles.map((f, i) => (
                            <div
                              key={i}
                              className="flex justify-between bg-gray-100 p-2 rounded"
                            >
                              <span>File {i + 1}</span>

                              <button
                                type="button"
                                onClick={() => removeFile(i)}
                                className="text-red-500"
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardSection>

                    {/* SUBMIT */}

                    <div className="flex justify-center mt-8">
                      <button
                        type="submit"
                        disabled={isUploading}
                        className="bg-blue-600 text-white px-8 py-2 rounded flex items-center gap-2"
                      >
                        {isUploading ? (
                          <>
                            <FaSpinner className="animate-spin" />
                            Uploading
                          </>
                        ) : (
                          "Submit"
                        )}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      )}
    </Popup>
  );
};

export default ArbitrationAction;
